import { format, parseISO } from 'date-fns';
import { ArrowLeft } from 'lucide-react';
import { Link } from 'react-router-dom';
import { cn } from '@/utils/cn';
import type { EventDetailsRow } from '../types';

interface EventDetailsHeaderProps {
  details: EventDetailsRow | null | undefined;
  eventCode: string;
  accountCode: string;
  isLoading: boolean;
  isError: boolean;
}

function formatEventDate(value: string | null | undefined): string {
  if (!value) return '-';
  try {
    return format(parseISO(value), 'M/d/yyyy');
  } catch {
    return value;
  }
}

export function EventDetailsHeader({
  details,
  eventCode,
  accountCode,
  isLoading,
  isError,
}: EventDetailsHeaderProps) {
  return (
    <header className="space-y-4 border-b border-cool-gray-2 pb-4">
      <div className="flex items-center gap-3">
        <Link
          to="/dashboard"
          aria-label="Back to dashboard"
          className="inline-flex h-9 w-9 items-center justify-center text-black hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-black focus:ring-offset-2"
        >
          <ArrowLeft aria-hidden="true" className="h-6 w-6" />
        </Link>
        <h1 id="event-details-heading" className="text-2xl font-bold leading-9 text-black">
          Event Details
        </h1>
      </div>

      {isLoading ? (
        <div className="space-y-2" aria-live="polite">
          <div className="h-5 w-64 animate-pulse bg-gray-200" />
          <div className="h-4 w-96 max-w-full animate-pulse bg-gray-200" />
        </div>
      ) : isError ? (
        <div role="alert" className="border border-dds-red bg-red-50 p-3 text-sm text-red-700">
          Failed to load event details. Please try again.
        </div>
      ) : details ? (
        <div>
          <p className="text-base font-bold text-black sm:text-lg">{details.name || '-'}</p>
          <p className="mt-1 text-sm text-cool-gray-11">
            {formatEventDate(details.startDateTime)} - {formatEventDate(details.endDateTime)}{' '}
            {'\u2022'} {details.eventCode || eventCode} {'\u2022'}{' '}
            <span
              className={cn(
                'inline-flex min-h-5 items-center px-1.5 text-xs font-bold leading-5 text-white',
                details.status ? 'bg-primary-600' : 'bg-cool-gray-11',
              )}
            >
              {details.status || 'Unknown'}
            </span>
          </p>
        </div>
      ) : (
        <div
          role="status"
          className="border border-cool-gray-2 bg-gray-50 p-3 text-sm text-cool-gray-11"
        >
          Event details were not found for {eventCode} / {accountCode}.
        </div>
      )}
    </header>
  );
}